import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

function ImageWithFallback(props: any) {
  const [didError, setDidError] = useState(false)
  const { src, alt, style, className, ...rest } = props

  return didError ? (
    <div
      className={`inline-block bg-gray-100 text-center align-middle ${className ?? ''}`}
      style={style}
    >
      <div className="flex items-center justify-center w-full h-full">
        <img src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODgiIGhlaWdodD0iODgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgc3Ryb2tlPSIjMDAwIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBvcGFjaXR5PSIuMyIgZmlsbD0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIzLjciPjxyZWN0IHg9IjE2IiB5PSIxNiIgd2lkdGg9IjU2IiBoZWlnaHQ9IjU2IiByeD0iNiIvPjxwYXRoIGQ9Im0xNiA1OCAxNi0xOCAzMiAzMiIvPjxjaXJjbGUgY3g9IjUzIiBjeT0iMzUiIHI9IjciLz48L3N2Zz4KCg==" alt="Error loading image" {...rest} data-original-url={src} />
      </div>
    </div>
  ) : (
    <img src={src} alt={alt} className={className} style={style} {...rest} onError={() => setDidError(true)} />
  )
}

interface TeamMemberProps {
  id: number;
  name: string;
  title: string;
  company: string;
  profileImage: string;
  buttonText?: string;
}

const TeamMember = ({ id, name, title, company, profileImage, buttonText = "View Profile" }: TeamMemberProps) => {
  const navigate = useNavigate();
  
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg hover:scale-105">
      <div className="flex flex-col items-center p-4">
        <div className="w-24 h-24 rounded-full overflow-hidden mb-4 border-2 border-green-100">
          <ImageWithFallback
            src={profileImage}
            alt={`${name}'s profile`}
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="text-center mb-4">
          <h3 className="font-semibold text-lg text-gray-800">{name}</h3>
          <p className="text-gray-600 text-sm">{title}</p>
          <p className="text-gray-500 text-xs">{company}</p>
        </div>
        
        <button 
          onClick={() => navigate(`/team/${id}`)}
          className="px-4 py-2 text-white rounded-full text-sm font-medium transition-colors w-full" 
          style={{ backgroundColor: '#22c55e' }} 
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#16a34a'} 
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#22c55e'}
        >
          Know More
        </button>
      </div>
    </div>
  );
};

interface ClinicalTeamProps {
  accentColor?: string;
  cardElevation?: number;
  buttonRadius?: number;
}

export default function ClinicalTeam({ accentColor = "#3B82F6", cardElevation = 1, buttonRadius = 16 }: ClinicalTeamProps) {
  const teamMembers = [
    {
      id: 1,
      name: "Dr Francis Selemo, HCPC",
      title: "Consultant Psychologist, CBT Therapist, Behavioural Couple Therapy Practitioner",
      company: "HealthCity UK",
      profileImage: require("../assets/selemo-Hteuky_k.png")
    },
    {
      id: 2,
      name: "Raffaella Lodigiani, HCPC",
      title: "Psychologist specialised in Cognitive Behavioural Therapy, Schema Therapy, Compassion Focused Therapy",
      company: "HealthCity UK",
      profileImage: require("../assets/raffaella-KNw2piZq.png")
    },
    {
      id: 3,
      name: "Miss Ilaria Giovanelli",
      title: "Clinical Psychologist, Educational Psychologist, Children's Neuropsychologist",
      company: "HealthCity UK",
      profileImage: require("../assets/iiaria-SXLkukrq.jpg")
    },
    {
      id: 4,
      name: "Dr Alina Luben",
      title: "EMDR Practitioner and Psychiatrist with CBT Skills",
      company: "HealthCity UK",
      profileImage: require("../assets/alina-QB0EtcZ8.jpeg")
    },
    {
      id: 5,
      name: "Mrs Manminder Hewey",
      title: "Senior Cognitive Behavioural Therapist & Trained EMDR Practitioner",
      company: "HealthCity UK",
      profileImage: require("../assets/manminder-JcdSCtr-.jpeg")
    },
    {
      id: 6,
      name: "Mrs Ellen Wheller",
      title: "Counselling Psychologist, EMDR Practitioner, CBT Practitioner, Therapeutic Consultant",
      company: "HealthCity UK",
      profileImage: require("../assets/ellen--4EI_3Ld.jpg")
    },
    {
      id: 7,
      name: "Miss Federica Zamuto",
      title: "Clinical Psychologist, CBT & DBT Practitioner, CAMHS Children and Adolescent Practitioner",
      company: "HealthCity UK",
      profileImage: require("../assets/federica-rFN0djea.jpg")
    },
    {
      id: 8,
      name: "Mr Raymond Vandy",
      title: "Cognitive Behavioural Therapist, Psychodynamic Counsellor, Couple Therapy Practitioner-Trainee",
      company: "HealthCity UK",
      profileImage: require("../assets/vandy-UCNr3lIG.png")
    },
    {
      id: 9,
      name: "Dr Helen Layton",
      title: "Clinical Psychologist, CBT Practitioner, Systemic Therapy Practitioner",
      company: "HealthCity UK",
      profileImage: require("../assets/layton-xLqgoDfz.jpeg")
    },
    {
      id: 10,
      name: "Mr Mohammad Shuja-Ul Hoda",
      title: "Psychologist specialised in Cognitive Behavioural Therapy, Schema Therapy, Compassion Focused Therapy",
      company: "HealthCity UK",
      profileImage: require("../assets/shuja-fkdw7Lo2.jpeg")
    },
    {
      id: 11,
      name: "Eva Tinka, MBPsS",
      title: "Psychologist and Partner",
      company: "HealthCity UK",
      profileImage: require("../assets/eva-6gzkC3eo.jpg")
    },
    {
      id: 12,
      name: "Edward Bbira",
      title: "Psychotherapist, Art Therapist, IAPT CBT & Systemic Family Practice Trained",
      company: "HealthCity UK",
      profileImage: require("../assets/edward-B7CPvc0v.png")
    },
    {
      id: 13,
      name: "Daniel Mirea",
      title: "CBT Consultant, Senior Lecturer & Senior Clinical Consultant",
      company: "HealthCity UK",
      profileImage: require("../assets/daniel-WNVVDztN.jpg")
    },
    {
      id: 14,
      name: "Cristina Sutac, HCPC",
      title: "Clinical Psychologist with CBT Therapy",
      company: "HealthCity UK",
      profileImage: require("../assets/cristina-938geM2Y.jpeg")
    },
    {
      id: 15,
      name: "Dr Anna Lukusa",
      title: "Medical Doctor & Health Trainer",
      company: "HealthCity UK",
      profileImage: require("../assets/anna-9_pihlyW.jpg")
    },
    {
      id: 16,
      name: "Dr Yomi Adetola",
      title: "Consultant Psychiatrist, Therapist, Trainer & Clinical Advisor",
      company: "HealthCity UK",
      profileImage: require("../assets/mark-EDd2Rsg-.jpg")
    },
    {
      id: 17,
      name: "Ms Emma Tierney MBACP",
      title: "Counsellor, Supervisor, CBT Practitioner, Mindfulness Practitioner, Senior Therapist",
      company: "HealthCity UK",
      profileImage: require("../assets/emma-KSxnxd_P.png")
    },
    {
      id: 18,
      name: "Danelle Devi-Hollenbeck",
      title: "Psychologist, EMDR Trained & Couple Therapy Trained",
      company: "HealthCity UK",
      profileImage: require("../assets/devi-yi8gZ1PP.png")
    },
    {
      id: 19,
      name: "Dr Deborah Hill, PhD",
      title: "EMDR Practitioner, Psychotherapist, Cognitive Behavioural Hypnotherapist",
      company: "HealthCity UK",
      profileImage: require("../assets/deborah-HghOQ6i9.jpg")
    },
    {
      id: 20,
      name: "Caroline Howard",
      title: "Cognitive Behavioural Psychotherapist & Clinical Consultant",
      company: "HealthCity UK",
      profileImage: require("../assets/caroline-BkxmoA5g.jpg")
    },
    {
      id: 21,
      name: "Helen Katali",
      title: "BABCP Accredited CBT Therapist, Perinatal Specialist Practitioner",
      company: "HealthCity UK",
      profileImage: require("../assets/katali-WEAdGzOc.jpg")
    },
    {
      id: 22,
      name: "Gail Bennett",
      title: "Jungian Analyst, Psychotherapist, Mindfulness-Based Cognitive Therapist, EMDR Practitioner",
      company: "HealthCity UK",
      profileImage: require("../assets/gail-jCEyDrHT.jpg")
    },
    {
      id: 23,
      name: "Monika Gos, CPsychol",
      title: "Clinical & Counselling Psychologist, EMDR and Brainspotting Therapist",
      company: "HealthCity UK",
      profileImage: require("../assets/monika-qTeGdN07.jpg")
    },
    {
      id: 24,
      name: "Dr Beverley Marais",
      title: "Specialist CBT Practitioner, Eating Disorder Specialist & Counselling Psychologist",
      company: "HealthCity UK",
      profileImage: require("../assets/marais-OuAQmxzY.jpg")
    },
    {
      id: 25,
      name: "Beatrice Salice",
      title: "Complementary Therapist and Clinical Consultant",
      company: "HealthCity UK",
      profileImage: require("../assets/beatrice-OY_PVpXK.jpg")
    },
    {
      id: 26,
      name: "Mark Howard",
      title: "Clinical Hypnotherapist and Clinical Consultant",
      company: "HealthCity UK",
      profileImage: require("../assets/mark-EDd2Rsg-.jpg")
    },
    {
      id: 27,
      name: "Kay Savage",
      title: "Complementary, Holistic & Wellbeing Therapist and Consultant",
      company: "HealthCity UK",
      profileImage: require("../assets/kay-7QwNUrHG.jpg")
    },
    {
      id: 28,
      name: "Natalia Bailey",
      title: "Senior Integrative Counsellor/Psychotherapist and Consultant",
      company: "HealthCity UK",
      profileImage: require("../assets/natalia-kC9X4jop.jpg")
    },
    {
      id: 29,
      name: "Lucy Arnold",
      title: "Counsellor/Psychotherapist & Clinical Specialist Partner",
      company: "HealthCity UK",
      profileImage: require("../assets/lucy-eNxnZ4D_.png")
    },
    {
      id: 30,
      name: "Sian Jones",
      title: "Person-to-Person Relationship Counsellor",
      company: "HealthCity UK",
      profileImage: require("../assets/sian-sBKE5e7j.jpg")
    },
    {
      id: 31,
      name: "Dinah Kissiedu",
      title: "Counsellor & Author",
      company: "HealthCity UK",
      profileImage: require("../assets/dinah-sEN91yU6.jpg")
    },
    {
      id: 32,
      name: "Ray Singh",
      title: "Cognitive Behavioural Hypnotherapist & Coach",
      company: "HealthCity UK",
      profileImage: require("../assets/ray-i7Y2nBrt.jpg")
    },
    {
      id: 33,
      name: "Naima Ali",
      title: "CBT Practitioner, Health Advocate and Clinical Consultant in Black & Minority Mental Health",
      company: "HealthCity UK",
      profileImage: require("../assets/naima-f5bhCzNv.jpg")
    },
    {
      id: 34,
      name: "Mrs Farida Yesmin Mezy",
      title: "Counsellor and Psychotherapist",
      company: "HealthCity UK",
      profileImage: require("../assets/farida-O6s_Uy8R.jpg")
    },
    {
      id: 35,
      name: "Lindita Nerguti",
      title: "Integrative Counsellor/Psychotherapist and Consultant",
      company: "HealthCity UK",
      profileImage: require("../assets/lindita-pTXxE9cI.jpg")
    },
    {
      id: 36,
      name: "Sonya Tancheya",
      title: "Cognitive Psychologist, Business Psychologist, CBT Practitioner, Research Assistant, Project Manager",
      company: "HealthCity UK",
      profileImage: require("../assets/sonya-wK8MEOFS.jpg")
    },
    {
      id: 37,
      name: "Teresa Blanco Martinez",
      title: "CBT and Integrative Therapy Practitioner & Psychologist",
      company: "HealthCity UK",
      profileImage: require("../assets/blanco-YOz96Y4u.jpg")
    },
    {
      id: 38,
      name: "Vincent Chidili",
      title: "CBT and Integrative Therapy Practitioner & Psychologist",
      company: "HealthCity UK",
      profileImage: require("../assets/vincent-M60L7WCG.jpg")
    }
  ];

  // Apply custom styles based on props
  const buttonStyle = {
    "--button-radius": `${buttonRadius}px`,
    "--accent-color": accentColor,
    "--card-elevation": cardElevation
  } as React.CSSProperties;

  return (
    <div className="w-full bg-gray-50 py-8 px-4" style={buttonStyle}>
      <div className="max-w-7xl mx-auto">
        <header className="mb-10">
          <h2 className="text-center text-2xl md:text-3xl font-bold">
            <span className="text-gray-400">CLINICAL</span>
            <span className="text-gray-800"> TEAM</span>
          </h2>
          <div className="w-24 h-1 bg-blue-500 mx-auto mt-3" style={{ backgroundColor: accentColor }}></div>
        </header>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {teamMembers.map(member => (
            <TeamMember
              key={member.id}
              id={member.id}
              name={member.name}
              title={member.title}
              company={member.company}
              profileImage={member.profileImage}
              buttonText="Know More"
            />
          ))}
        </div>
      </div>
    </div>
  );
}